import React, { useState, useEffect } from 'react';
import BreadCrumb from 'Common/BreadCrumb';
import { SchoolsService } from '../../services/schoolsService';
import { School } from '../../types/school';
import { Plus, Edit, Search, Loader } from 'lucide-react';
import { toast } from 'react-toastify';
import AddSchoolDialog from './AddSchoolDialog';
import EditSchoolDialog from './EditSchoolDialog';

const Schools = () => {
  const [schools, setSchools] = useState<School[]>([]);
  const [filteredSchools, setFilteredSchools] = useState<School[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [schoolsPerPage] = useState(10);
  const [isLoadingSchools, setIsLoadingSchools] = useState(false);

  const [showAddSchoolDialog, setShowAddSchoolDialog] = useState(false);
  const [showEditSchoolDialog, setShowEditSchoolDialog] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState<School | null>(null);

  useEffect(() => {
    loadSchools();
  }, []);

  const loadSchools = async () => {
    try {
      setIsLoadingSchools(true);
      const schoolsData = await SchoolsService.getAllSchools();
      setSchools(schoolsData);
      setFilteredSchools(schoolsData);
    } catch (error) {
      console.error('Error loading schools:', error);
      toast.error('Failed to load schools', {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsLoadingSchools(false);
    }
  };

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredSchools(schools);
    } else {
      const filtered = schools.filter(school =>
        school.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredSchools(filtered);
    }
    setCurrentPage(1); 
  }, [searchQuery, schools]);

  const handleAddSchool = () => {
    setShowAddSchoolDialog(true);
  };

  const handleEditSchool = (school: School) => {
    setSelectedSchool(school);
    setShowEditSchoolDialog(true);
  };

  const handleSaveSchool = async (school: School) => {
    try {
      await SchoolsService.createSchool(school);
      await loadSchools();
      toast.success('School added successfully!', {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (error) {
      console.error('Error saving school:', error);
      toast.error('Failed to add school. Please try again.', {
        position: "top-right",
        autoClose: 3000,
      });
      throw error;
    }
  };

  const handleUpdateSchool = async (updatedSchool: School) => {
    try {
      await SchoolsService.updateSchool(updatedSchool);
      await loadSchools();
      toast.success('School updated successfully!', {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (error) {
      console.error('Error updating school:', error);
      toast.error('Failed to update school. Please try again.', {
        position: "top-right",
        autoClose: 3000,
      });
      throw error;
    }
  };

  const indexOfLastSchool = currentPage * schoolsPerPage;
  const indexOfFirstSchool = indexOfLastSchool - schoolsPerPage;
  const currentSchools = filteredSchools.slice(indexOfFirstSchool, indexOfLastSchool);
  const totalPages = Math.ceil(filteredSchools.length / schoolsPerPage);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  return (
    <React.Fragment>
      <BreadCrumb title='Schools' pageTitle='Dashboard' />

      <div className="card">
        <div className="card-body">
          <div className="flex items-center justify-between mb-4">
            <h6 className="text-15 font-semibold">Schools Management</h6>
            <button
              onClick={handleAddSchool}
              className="px-4 py-2 text-sm bg-custom-500 text-white hover:bg-custom-600 rounded-md transition-colors flex items-center gap-2"
            >
              <Plus className="size-4" />
              Add School
            </button>
          </div>

          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 size-4 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search schools..."
                className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:border-custom-500 dark:bg-zink-700 dark:border-zink-500"
              />
            </div>
          </div>

          {isLoadingSchools ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="size-6 animate-spin text-custom-500" />
              <span className="ml-2 text-slate-500">Loading schools...</span>
            </div>
          ) : filteredSchools.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-500 dark:text-zink-400">
                {searchQuery ? 'No schools found matching your search.' : 'No schools available. Click "Add School" to create one.'}
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-100 dark:bg-zink-600">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-700 dark:text-zink-200">
                        #
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-700 dark:text-zink-200">
                        School Name
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-700 dark:text-zink-200">
                        Created At
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-slate-700 dark:text-zink-200">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-zink-500">
                    {currentSchools.map((school, index) => (
                      <tr key={school.id} className="hover:bg-slate-50 dark:hover:bg-zink-600/50">
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-zink-300">
                          {indexOfFirstSchool + index + 1}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-800 dark:text-zink-100 font-medium">
                          {school.name}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-zink-300">
                          {new Date(school.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })}
                        </td>
                        <td className="px-4 py-3 text-sm text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleEditSchool(school)}
                              className="p-2 text-slate-600 hover:text-custom-500 dark:text-zink-300 dark:hover:text-custom-500 transition-colors"
                              title="Edit"
                            >
                              <Edit className="size-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-200 dark:border-zink-500">
                  <p className="text-sm text-slate-600 dark:text-zink-300">
                    Showing {indexOfFirstSchool + 1} to {Math.min(indexOfLastSchool, filteredSchools.length)} of {filteredSchools.length} schools
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-3 py-1 text-sm border border-slate-200 dark:border-zink-500 rounded-md hover:bg-slate-100 dark:hover:bg-zink-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Previous
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-3 py-1 text-sm rounded-md transition-colors ${
                          currentPage === page
                            ? 'bg-custom-500 text-white'
                            : 'border border-slate-200 dark:border-zink-500 hover:bg-slate-100 dark:hover:bg-zink-600'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 text-sm border border-slate-200 dark:border-zink-500 rounded-md hover:bg-slate-100 dark:hover:bg-zink-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <AddSchoolDialog
        show={showAddSchoolDialog}
        onClose={() => setShowAddSchoolDialog(false)}
        onSave={handleSaveSchool}
      />
      
      {selectedSchool && (
        <EditSchoolDialog
          show={showEditSchoolDialog}
          school={selectedSchool}
          onClose={() => setShowEditSchoolDialog(false)}
          onSave={handleUpdateSchool}
        />
      )}
    </React.Fragment>
  );
};

export default Schools;
